/**
 * @author assistant
 * @date 2025.01.08
 * @description 복비 입금 신청 공통 컴포넌트 (모달/페이지 공용)
 */
import React, {useEffect, useState} from 'react';
import Button from '../../button/Button';
import Input from '../../form/Input';
import Select from '../../form/Select';
import { toast } from 'react-toastify';
import { useBokbiHistoryStore } from '../../../store/useBokbiHistory';
import { IBokbiDepositRequest } from '../../../service/bokbiAPI';
import {useFranchiseStore} from "../../../store/useFranchise";
import { useNavigate } from 'react-router-dom';

interface IBokbiDepositContent {
    onSubmit?: (data: IDepositData) => void;
    onCancel?: () => void;
    showCancelButton?: boolean;
}

interface IDepositData {
    depositorName: string;
    depositBank: string;
    depositAmount: number;
}

interface ITaxInvoiceData {
    companyName: string;
    companyNumber: string;
    representativeName: string;
    businessAddress: string;
    email: string;
}

const BokbiDepositContent: React.FC<IBokbiDepositContent> = ({ 
    onSubmit, 
    onCancel,
    showCancelButton = false 
}) => {
    const navigate = useNavigate();
    const { createDeposit, createDepositLoading } = useBokbiHistoryStore();
    const { myfranchise } = useFranchiseStore()
    const [formData, setFormData] = useState<IDepositData>({
        depositorName: '',
        depositBank: '',
        depositAmount: 0
    });
    const [isEditingTaxInfo, setIsEditingTaxInfo] = useState(false);
    const [taxInvoiceData, setTaxInvoiceData] = useState<ITaxInvoiceData>({
        companyName: '',
        companyNumber: '',
        representativeName: '',
        businessAddress: '',
        email: ''
    });

    const bankOptions = [
        { value: '', option: '은행 선택' },
        { value: 'KB국민은행', option: 'KB국민은행' },
        { value: '신한은행', option: '신한은행' },
        { value: '하나은행', option: '하나은행' },
        { value: '우리은행', option: '우리은행' },
        { value: 'NH농협은행', option: 'NH농협은행' },
        { value: '카카오뱅크', option: '카카오뱅크' },
        { value: '토스뱅크', option: '토스뱅크' },
        { value: 'IBK기업은행', option: 'IBK기업은행' },
        { value: '케이뱅크', option: '케이뱅크' },
        { value: 'SC제일은행', option: 'SC제일은행' }
    ];

    const handleSubmit = async () => {
        if (!formData.depositorName || !formData.depositBank ||
            !taxInvoiceData.companyName || !taxInvoiceData.representativeName ||
            !taxInvoiceData.businessAddress || !taxInvoiceData.companyNumber || !taxInvoiceData.email
        ) {
            toast.error('모든 항목을 입력해주세요.');
            return;
        }

        // 최소 금액 검증
        if (formData.depositAmount < 1) {
            toast.error('복비 충전은 1원부터 가능합니다.');
            return;
        }

        // 최대 금액 검증
        if (formData.depositAmount > 10000000) {
            toast.error('복비 충전의 최대금액은 10,000,000원입니다.');
            return;
        }

        // 날짜와 시간을 ISO datetime 형식으로 변환
        const requestData: IBokbiDepositRequest = {
            depositName: formData.depositorName,
            depositBank: formData.depositBank,
            depositAmount: formData.depositAmount,
            description: '',
            companyNumber: taxInvoiceData.companyNumber,
            companyName: taxInvoiceData.companyName,
            ceo: taxInvoiceData.representativeName,
            email: taxInvoiceData.email,
            address: taxInvoiceData.businessAddress
        };

        const success = await createDeposit(requestData);
        
        if (success) {
            if (onSubmit) {
                onSubmit(formData);
            }
            // 폼 초기화
            setFormData({
                depositorName: '',
                depositBank: '',
                depositAmount: 0
            });
            setIsEditingTaxInfo(false);
            navigate('/bokbi/purchase-order')
        }
    };

    useEffect(() => {
        if (myfranchise) {
            setTaxInvoiceData({
                companyName: myfranchise.companyName || '',
                companyNumber: myfranchise.companyNumber || '',
                representativeName: myfranchise.ceo || '',
                businessAddress: myfranchise.address || '',
                email: myfranchise.email || ''
            });
        }
    }, [myfranchise]);

    // 사업자번호 포매팅 함수
    const formatBusinessNumber = (value: string) => {
        let businessInput = value.replace(/\D/g, '');

        if (businessInput.length > 3 && businessInput.length <= 5) {
            businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3)}`;
        } else if (businessInput.length > 5) {
            businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3, 5)}-${businessInput.slice(5, 10)}`;
        }

        return businessInput;
    };

    return (
        <>
            <div className="saju_deposit_info">
                <h3>입금계좌 정보</h3>
                <div className="saju_deposit_info_box">
                    <p><strong>은행명:</strong> 우리은행</p>
                    <p><strong>계좌번호:</strong> 1005-004-856117</p>
                    <p><strong>예금주:</strong> 주식회사 앤유코퍼레이션</p>
                </div>
                <p className="text-red-600 text-sm">복비 충전의 최대금액은 10,000,000원 (1,000만원)입니다.
                </p>
                <p className="text-red-600 text-sm">초과 입금 시 입금 승인이 거절될 수 있습니다.</p>
            </div>

            <div className="saju_modal_body">
                <h3>입금 정보 작성</h3>

                <div className="saju_form_group">
                    <label>입금자명</label>
                    <Input
                        value={formData.depositorName}
                        onChange={(e) => setFormData({...formData, depositorName: e.target.value})}
                        placeholder="입금자명을 입력하세요"
                        width={300}
                    />
                </div>

                <div className="saju_form_group">
                    <label>입금 은행</label>
                    <Select
                        value={formData.depositBank}
                        onChange={(e) => setFormData({...formData, depositBank: e.target.value})}
                        options={bankOptions}
                        style={{ width: 300 }}
                    />
                </div>

                <div className="saju_form_group">
                    <label>입금액</label>
                    <Input
                        type="number"
                        value={formData.depositAmount ? String(formData.depositAmount) : ''}
                        onChange={(e) => setFormData({...formData, depositAmount: Number(e.target.value)})}
                        placeholder="입금액을 입력하세요"
                        width={300}
                    />
                </div>
                <div className="saju_tax_invoice_section">
                    <div className="saju_tax_invoice_header">
                        <h3>세금계산서 발행 정보</h3>
                        {!isEditingTaxInfo && (
                            <button 
                                className="saju_edit_button"
                                onClick={() => setIsEditingTaxInfo(true)}
                            >
                                수정하기
                            </button>
                        )}
                    </div>
                    
                    {!isEditingTaxInfo ? (
                        <div className="saju_tax_invoice_info">
                            <div className="saju_tax_info_item">
                                <label>사업자명:</label>
                                <span>{taxInvoiceData.companyName || '-'}</span>
                            </div>
                            <div className="saju_tax_info_item">
                                <label>사업자번호:</label>
                                <span>{taxInvoiceData.companyNumber || '-'}</span>
                            </div>
                            <div className="saju_tax_info_item">
                                <label>대표자명:</label>
                                <span>{taxInvoiceData.representativeName || '-'}</span>
                            </div>
                            <div className="saju_tax_info_item">
                                <label>사업장주소:</label>
                                <span>{taxInvoiceData.businessAddress || '-'}</span>
                            </div>
                            <div className="saju_tax_info_item">
                                <label>이메일:</label>
                                <span>{taxInvoiceData.email || '-'}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="saju_tax_invoice_edit">
                            <div className="saju_form_group">
                                <label>사업자명</label>
                                <Input
                                    value={taxInvoiceData.companyName}
                                    onChange={(e) => setTaxInvoiceData({...taxInvoiceData, companyName: e.target.value})}
                                    placeholder="사업자명을 입력하세요"
                                    width={300}
                                />
                            </div>
                            <div className="saju_form_group">
                                <label>사업자번호</label>
                                <Input
                                    value={taxInvoiceData.companyNumber}
                                    onChange={(e) => setTaxInvoiceData({...taxInvoiceData, companyNumber: formatBusinessNumber(e.target.value)})}
                                    placeholder="사업자 번호를 입력하세요 (예: 123-45-67890)"
                                    width={300}
                                />
                            </div>
                            <div className="saju_form_group">
                                <label>대표자명</label>
                                <Input
                                    value={taxInvoiceData.representativeName}
                                    onChange={(e) => setTaxInvoiceData({...taxInvoiceData, representativeName: e.target.value})}
                                    placeholder="대표자명을 입력하세요"
                                    width={300}
                                />
                            </div>
                            <div className="saju_form_group">
                                <label>사업장주소</label>
                                <Input
                                    value={taxInvoiceData.businessAddress}
                                    onChange={(e) => setTaxInvoiceData({...taxInvoiceData, businessAddress: e.target.value})}
                                    placeholder="사업장 주소를 입력하세요"
                                    width={400}
                                />
                            </div>
                            <div className="saju_form_group">
                                <label>이메일</label>
                                <Input
                                    type="email"
                                    value={taxInvoiceData.email}
                                    onChange={(e) => setTaxInvoiceData({...taxInvoiceData, email: e.target.value})}
                                    placeholder="example@example.com"
                                    width={300}
                                />
                            </div>
                            <div className="saju_tax_edit_buttons">
                                <button 
                                    className="saju_cancel_button"
                                    onClick={() => {
                                        if (myfranchise) {
                                            setTaxInvoiceData({
                                                companyName: myfranchise.companyName || '',
                                                companyNumber: myfranchise.companyNumber || '',
                                                representativeName: myfranchise.ceo || '',
                                                businessAddress: myfranchise.address || '',
                                                email: myfranchise.email || ''
                                            });
                                        }
                                        setIsEditingTaxInfo(false);
                                    }}
                                >
                                    취소
                                </button>
                                <button 
                                    className="saju_confirm_button"
                                    onClick={() => setIsEditingTaxInfo(false)}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <p className="text-sm">입금 승인을 위해 정확한 정보를 입력해주세요. 제대로 입력되지 않은 경우 입금 승인이 거절될 수 있습니다.</p>
                <p className="text-sm">입금 확인까지 시간이 소요될 수 있습니다.</p>
            </div>

            <div className="saju_modal_footer">
                <Button
                    type="submit"
                    text={createDepositLoading ? "신청 중..." : "신청하기"}
                    onClick={handleSubmit}
                    disabled={createDepositLoading}
                />
            </div>
        </>
    );
};

export default BokbiDepositContent;